import { createFileRoute } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  formatCents,
  subscriptionStatusLabel,
  isSubscriptionActive,
  needsPaymentAttention,
} from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import {
  useSubscriptionPlans,
  useFamilySubscriptions,
  useCreateCheckoutSession,
  useOpenCustomerPortal,
} from '@/data/stripe';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';
import { MobilePaymentNotice } from '@/components/ui/MobilePaymentNotice';
import { arePaymentsEnabled } from '@/lib/native/payments';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/parent/billing/auto-pay')({
  component: ParentAutoPay,
});

function useMyFamilyId() {
  return useQuery({
    queryKey: ['my-family-id'],
    queryFn: async (): Promise<string | null> => {
      const u = await supabase.auth.getUser();
      return (u.data.user?.app_metadata.family_id as string | undefined) ?? null;
    },
  });
}

function ParentAutoPay() {
  const myFamily = useMyFamilyId();
  const familyId = myFamily.data ?? undefined;
  const plans = useSubscriptionPlans({ activeOnly: true });
  const subs = useFamilySubscriptions(familyId);
  const checkout = useCreateCheckoutSession();
  const portal = useOpenCustomerPortal();
  const paymentsEnabled = arePaymentsEnabled();

  const activeSub = (subs.data ?? []).find((s) => isSubscriptionActive(s.status));

  async function enrollPlan(planId: string) {
    try {
      const success = `${window.location.origin}/parent/billing/checkout-return?mode=subscription&status=success`;
      const cancel = `${window.location.origin}/parent/billing/auto-pay`;
      const resp = await checkout.mutateAsync({
        mode: 'subscription',
        plan_id: planId,
        success_url: success,
        cancel_url: cancel,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast({
        title: "Couldn't start checkout",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  async function openPortal() {
    try {
      const resp = await portal.mutateAsync({
        return_url: `${window.location.origin}/parent/billing/auto-pay`,
      });
      window.location.href = resp.url;
    } catch (err) {
      toast({
        title: "Couldn't open portal",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Billing
        </p>
        <h1 className="mt-2">Auto-pay</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Enroll in monthly auto-pay so tuition is charged automatically each cycle. Cancel or
          update your card anytime through the customer portal.
        </p>
      </header>

      {activeSub && (
        <section aria-labelledby="active-sub-heading" className="surface-elevated space-y-3 p-6">
          <header className="flex items-baseline justify-between gap-3">
            <h2
              id="active-sub-heading"
              className="font-[var(--font-display)] text-[var(--text-xl)]"
            >
              Active subscription
            </h2>
            <Badge tone={needsPaymentAttention(activeSub.status) ? 'warn' : 'success'}>
              {subscriptionStatusLabel(activeSub.status)}
            </Badge>
          </header>
          <p className="text-[var(--color-ink-muted)]">
            {activeSub.current_period_end ? (
              <>
                Next charge:{' '}
                <span className="tabular-nums">
                  {new Date(activeSub.current_period_end).toLocaleDateString()}
                </span>
              </>
            ) : (
              'Pending first charge.'
            )}
            {activeSub.cancel_at_period_end && ' · Will not renew.'}
          </p>
          {needsPaymentAttention(activeSub.status) && (
            <p className="text-[var(--color-danger)] text-[var(--text-sm)]">
              We couldn't collect your latest payment. Open the portal to update your card or
              method.
            </p>
          )}
          {paymentsEnabled ? (
            <Button onClick={() => void openPortal()} disabled={portal.isPending}>
              {portal.isPending ? 'Opening…' : 'Manage subscription in portal'}
            </Button>
          ) : (
            <MobilePaymentNotice action="manage auto-pay" />
          )}
        </section>
      )}

      {!activeSub && (
        <section aria-labelledby="plans-heading">
          <h2 id="plans-heading" className="text-[var(--text-xl)]">
            Choose a plan
          </h2>
          {plans.isLoading && <p aria-live="polite">Loading plans…</p>}
          {!plans.isLoading && (plans.data?.length ?? 0) === 0 && (
            <Empty
              title="No plans configured"
              description="Ask the studio to enable auto-pay tiers in their admin area."
              className="mt-4"
            />
          )}
          <ul className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(plans.data ?? []).map((p) => (
              <li key={p.id} className="surface flex h-full flex-col justify-between p-5">
                <div>
                  <h3 className="font-[var(--font-display)] text-[var(--text-lg)]">{p.name}</h3>
                  <p className="mt-1 font-[var(--font-display)] text-[var(--text-2xl)] tabular-nums">
                    {formatCents(p.monthly_cents)}{' '}
                    <span className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">/mo</span>
                  </p>
                  {p.description && (
                    <p className="mt-3 text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                      {p.description}
                    </p>
                  )}
                </div>
                {paymentsEnabled ? (
                  <Button
                    className="mt-4"
                    onClick={() => void enrollPlan(p.id)}
                    disabled={checkout.isPending}
                  >
                    Enroll
                  </Button>
                ) : (
                  <MobilePaymentNotice action="set up auto-pay" />
                )}
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
